import {
  Box,
  Button,
  Grid2,
  InputLabel,
  Modal,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import colors from "../../../theme/Colours";
import React, { useEffect, useRef, useState } from "react";
import crossIcon from "../../../assets/crossIcon.png.png";
import { BeerFormStateEdit } from "../../../constants/types";
import { uploadButton, uploadButtonContainer } from "./productStyles";
import { Switch } from "../../../common/Switch";
import { ReactComponent as CloudUploadIcon } from "../../../assets/CloudUpload.svg";

export interface productFormProps extends BeerFormStateEdit {
  buttonTextOne: string;
  buttonTextTwo: string;
  formTitle: string;
  isEdit: boolean;
  onSubmit: (data: BeerFormStateEdit) => void;
}

const StyledTextField = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBottom: "4px",
});

const StyledInput = styled("input")({
  paddingLeft: "5px",
  height: "7vh",
  width: "95%",
  border: "1px solid #c5ced6",
  borderRadius: "8px",
  // Hide the up/down spinner on number inputs to prevent accidental value changes
  "&[type=number]": {
    MozAppearance: "textfield",
  },
  "&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
});

// Prevent the mouse wheel from changing the value of a focused number input.
// Browsers natively change number-input values when the user scrolls over them,
// which makes it look like values are mysteriously changing when scrolling the form.
const preventWheelChange = (e: React.WheelEvent<HTMLInputElement>) => {
  (e.target as HTMLInputElement).blur();
};

const FileTab = styled("div")({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#EFEFEF",
  border: "2px solid #767676",
  padding: "5px",
  margin: "5px",
  fontSize: "12px",
  gap: "5px",
  height: "20px",
  overflow: "hidden",
  fontFamily: "Roboto",
});

const ProductForm: React.FC<productFormProps> = ({
  beerName,
  description,
  fileUpload,
  quantity,
  price,
  basePrice,
  ed,
  onSubmit,
  buttonTextOne,
  buttonTextTwo,
  formTitle,
  existingImages,
  isEdit,
  abv,
  ibu,
  preorder,
  displayInHome: displayInHomeProp,
  quantity_20L,
  quantity_30L,
  quantity_50L,
}) => {
  const [open, setOpen] = useState(false);
  const [preBooking, setPreBooking] = useState(!!preorder);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    setPreBooking(!!preorder);
  }, [preorder]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [formData, setFormData] = useState<BeerFormStateEdit>({
    beerName: beerName || "",
    quantity: quantity || "",
    description: description || "",
    price: price || "",
    basePrice: basePrice ?? 0,
    ed: ed ?? 0,
    fileUpload: fileUpload || [],
    existingImages: existingImages || [],
    abv: abv || "",
    ibu: ibu || "",
    quantity_20L: 0,
    quantity_30L: 0,
    quantity_50L: 0,
  });
  const [fileCount, setFileCount] = useState(
    existingImages.length + fileUpload.length || 0
  );

  useEffect(() => {
    setFileCount(
      formData.existingImages.length + formData.fileUpload.length || 0
    );
  }, [formData.existingImages, formData.fileUpload]);

  useEffect(() => {
    if (!isEdit) return;
    const newFormData = {
      beerName: beerName || "",
      quantity: quantity || "",
      description: description || "",
      price: price || "",
      basePrice: basePrice ?? 0,
      ed: ed ?? 0,
      fileUpload: fileUpload || [],
      existingImages: existingImages || [],
      abv: abv || "",
      ibu: ibu || "",
      quantity_20L: quantity_20L ?? 0,
      quantity_30L: quantity_30L ?? 0,
      quantity_50L: quantity_50L ?? 0,
    };
    setFormData(newFormData);
  }, [
    isEdit,
    beerName,
    quantity,
    description,
    price,
    basePrice,
    ed,
    fileUpload,
    existingImages,
    abv,
    ibu,
    quantity_20L,
    quantity_30L,
    quantity_50L,
  ]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateField = (name: string, value: string): string => {
    const val = value.trim();
    switch (name) {
      case "beerName":
        if (!val) return "Beer name is required";
        if (!/^[a-zA-Z0-9\s\-&.]{2,100}$/.test(val))
          return "Beer name should contain 2\u2013100 characters. Letters, numbers, spaces, '-', '&', and '.' are allowed.";
        break;
      case "abv":
        if (!val) return "ABV is required";
        if (!/^\d{1,2}(\.\d{1,2})?$/.test(val))
          return "Enter ABV between 0\u201399 with up to 2 decimal places (Example: 5.5).";
        break;
      case "ibu":
        if (!val) return "IBU is required";
        if (!/^\d{1,3}(\.\d{1,2})?$/.test(val))
          return "Enter IBU value between 0\u2013999 with up to 2 decimal places.";
        break;
      case "price":
        if (!val) return "Beer price is required";
        if (!/^\d{1,6}(\.\d{1,2})?$/.test(val))
          return "Enter a positive price with up to 2 decimal places (Example: 250.00).";
        break;
      case "basePrice":
        if (val === "") return "Base price is required";
        if (!/^\d{1,6}(\.\d{1,2})?$/.test(val))
          return "Enter a positive price with up to 2 decimal places (Example: 250.00).";
        break;
      case "ed":
        if (val !== "" && !/^\d{1,6}(\.\d{1,2})?$/.test(val))
          return "Enter a valid ED (Excise Duty).";
        break;
      case "description":
        if (value.length > 1000)
          return "Description can contain up to 1000 characters.";
        break;
      case "quantity_20L":
      case "quantity_30L":
      case "quantity_50L":
        if (val !== "" && !/^\d{1,6}$/.test(val))
          return "Enter a non-negative whole number only. Decimals are not allowed.";
        break;
    }
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData((prevData) => ({
        ...prevData,
        fileUpload: [...prevData.fileUpload, ...newFiles],
      }));
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFormData((prevData) => ({
      ...prevData,
      fileUpload: prevData.fileUpload.filter((file) => file !== fileToRemove),
    }));
  };

  const handleRemoveFileUrl = (fileToRemove: any) => {
    setFormData((prevData) => ({
      ...prevData,
      existingImages: prevData.existingImages.filter(
        (file) => file.url !== fileToRemove.url
      ),
    }));
  };

  const handleReset = () => {
    setFormData({
      beerName: "",
      quantity: "",
      description: "",
      price: "",
      basePrice: 0,
      ed: 0,
      fileUpload: [],
      existingImages: [],
      abv: "",
      ibu: "",
      quantity_20L: 0,
      quantity_30L: 0,
      quantity_50L: 0,
    });
    setPreBooking(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    handleClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    const beerNameVal = String(formData.beerName || "").trim();
    if (!beerNameVal) {
      newErrors.beerName = "Beer name is required";
    } else if (!/^[a-zA-Z0-9\s\-&.]{2,100}$/.test(beerNameVal)) {
      newErrors.beerName =
        "Beer name should contain 2\u2013100 characters. Letters, numbers, spaces, '-', '&', and '.' are allowed.";
    }

    const abvVal = String(formData.abv || "").trim();
    if (!abvVal) {
      newErrors.abv = "ABV is required";
    } else if (!/^\d{1,2}(\.\d{1,2})?$/.test(abvVal)) {
      newErrors.abv =
        "Enter ABV between 0\u201399 with up to 2 decimal places (Example: 5.5).";
    }

    const ibuVal = String(formData.ibu || "").trim();
    if (!ibuVal) {
      newErrors.ibu = "IBU is required";
    } else if (!/^\d{1,3}(\.\d{1,2})?$/.test(ibuVal)) {
      newErrors.ibu =
        "Enter IBU value between 0\u2013999 with up to 2 decimal places.";
    }

    const priceVal = String(formData.price || "").trim();
    if (!priceVal) {
      newErrors.price = "Beer price is required";
    } else if (!/^\d{1,6}(\.\d{1,2})?$/.test(priceVal)) {
      newErrors.price =
        "Enter a positive price with up to 2 decimal places (Example: 250.00).";
    }

    const basePriceVal = String(formData.basePrice ?? "").trim();
    if (basePriceVal === "") {
      newErrors.basePrice = "Base price is required";
    } else if (!/^\d{1,6}(\.\d{1,2})?$/.test(basePriceVal)) {
      newErrors.basePrice =
        "Enter a positive price with up to 2 decimal places (Example: 250.00).";
    }

    const edVal = String(formData.ed ?? "").trim();
    if (edVal !== "" && !/^\d{1,6}(\.\d{1,2})?$/.test(edVal)) {
      newErrors.ed = "Enter a valid ED (Excise Duty).";
    }

    const descVal = String(formData.description || "");
    if (descVal.length > 1000) {
      newErrors.description = "Description can contain up to 1000 characters.";
    }

    const qty20 = String(formData.quantity_20L ?? "");
    if (qty20 !== "" && !/^\d{1,6}$/.test(qty20)) {
      newErrors.quantity_20L =
        "Enter a non-negative whole number only. Decimals are not allowed.";
    }
    const qty30 = String(formData.quantity_30L ?? "");
    if (qty30 !== "" && !/^\d{1,6}$/.test(qty30)) {
      newErrors.quantity_30L =
        "Enter a non-negative whole number only. Decimals are not allowed.";
    }
    const qty50 = String(formData.quantity_50L ?? "");
    if (qty50 !== "" && !/^\d{1,6}$/.test(qty50)) {
      newErrors.quantity_50L =
        "Enter a non-negative whole number only. Decimals are not allowed.";
    }

    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
    const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
    const invalidFiles = formData.fileUpload.filter(
      (f) => !ALLOWED_IMAGE_TYPES.includes(f.type) || f.size > MAX_IMAGE_SIZE
    );
    if (invalidFiles.length > 0) {
      newErrors.fileUpload =
        "Upload only JPG, PNG, or WEBP images. Recommended maximum size is 5MB per file.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      ...formData,
      preorder: preBooking,
      displayInHome: preBooking,
    });
  };
  return (
    <div>
      <form
        style={{
          padding: "48px 32px",
          background: "white",
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          flexDirection: "column",
        }}
        onSubmit={handleSubmit}
      >
        <div>
          <Typography
            variant="h1"
            style={{
              fontSize: "18px",
              marginLeft: "0px",
              marginBottom: "24px",
              fontFamily: "Montserrat",
            }}
          >
            {formTitle}
          </Typography>
        </div>
        <Grid2
          container
          spacing={3}
          margin={0}
          fontFamily={"Roboto"}
          sx={{ fontFamily: "Roboto" }}
        >
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="beerName"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                Beer Name<span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>

              <StyledInput
                placeholder="Beer Name"
                type="text"
                id="beerName"
                data-testid="beerName1"
                name="beerName"
                value={formData.beerName}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.beerName && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.beerName}
                </span>
              )}
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="abv"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                ABV (Alcohol by Volume)
                <span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>
              <StyledInput
                placeholder="Please Enter ABV value"
                type="number"
                id="abv"
                name="abv"
                value={formData.abv}
                onChange={handleChange}
                onBlur={handleBlur}
                onWheel={preventWheelChange}
                required
              />
              {errors.abv && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.abv}
                </span>
              )}
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="ibu"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                IBU (International Bitterness Units)
                <span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>
              <StyledInput
                placeholder="Please Enter IBU value"
                type="number"
                id="ibu"
                name="ibu"
                value={formData.ibu}
                onChange={handleChange}
                onBlur={handleBlur}
                onWheel={preventWheelChange}
                required
              />
              {errors.ibu && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.ibu}
                </span>
              )}
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="price"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                Beer Price<span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>
              <StyledInput
                placeholder="Beer Price"
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                onBlur={handleBlur}
                onWheel={preventWheelChange}
                required
              />
              {errors.price && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.price}
                </span>
              )}
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="price"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                Base Price<span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>
              <StyledInput
                placeholder="Base Price"
                type="number"
                id="basePrice"
                name="basePrice"
                value={formData.basePrice || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                onWheel={preventWheelChange}
                required
              />
              {errors.basePrice && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.basePrice}
                </span>
              )}
            </StyledTextField>
          </Grid2>
          <Grid2 size={[12, 6, 4]}>
            <StyledTextField>
              <InputLabel
                htmlFor="price"
                sx={{ fontFamily: "Roboto", fontWeight: "500" }}
              >
                ED (Excise Duty)<span style={{ color: "#c0392b" }}>*</span>
              </InputLabel>
              <StyledInput
                placeholder="ED value"
                type="number"
                id="ed"
                name="ed"
                value={formData.ed || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                onWheel={preventWheelChange}
                required
              />
              {errors.ed && (
                <span style={{ color: "#ef4444", fontSize: "11px" }}>
                  {errors.ed}
                </span>
              )}
            </StyledTextField>
          </Grid2>

          <Grid2 size={12}>
            <InputLabel
              htmlFor="description"
              sx={{
                fontFamily: "Roboto",
                fontWeight: "500",
                marginBottom: "5px",
              }}
            >
              Description
            </InputLabel>
            <TextField
              multiline
              rows={4}
              placeholder="Description"
              data-testid="Description"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              sx={{
                width: "100%",
                borderRadius: "4px",
                border: "1px solid #C5CED6",
                "& .MuiInputBase-input": {
                  color: "black",
                },
              }}
            />
            {errors.description && (
              <span style={{ color: "#ef4444", fontSize: "11px" }}>
                {errors.description}
              </span>
            )}
          </Grid2>

          <Grid2 size={12}>
            <InputLabel
              sx={{
                fontFamily: "Roboto",
                fontWeight: "500",
                marginBottom: "5px",
              }}
            >
              Inventory
            </InputLabel>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: { xs: "wrap", md: "nowrap" },
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <StyledTextField>
                <InputLabel
                  htmlFor="quantity_20L"
                  sx={{ fontFamily: "Roboto", fontWeight: "500" }}
                >
                  20L Keg
                </InputLabel>
                <StyledInput
                  placeholder="0"
                  type="number"
                  id="quantity_20L"
                  name="quantity_20L"
                  value={formData.quantity_20L || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onWheel={preventWheelChange}
                />
                {errors.quantity_20L && (
                  <span style={{ color: "#ef4444", fontSize: "11px" }}>
                    {errors.quantity_20L}
                  </span>
                )}
              </StyledTextField>
              <StyledTextField>
                <InputLabel
                  htmlFor="quantity_30L"
                  sx={{ fontFamily: "Roboto", fontWeight: "500" }}
                >
                  30L Keg
                </InputLabel>
                <StyledInput
                  placeholder="0"
                  type="number"
                  id="quantity_30L"
                  name="quantity_30L"
                  value={formData.quantity_30L || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onWheel={preventWheelChange}
                />
                {errors.quantity_30L && (
                  <span style={{ color: "#ef4444", fontSize: "11px" }}>
                    {errors.quantity_30L}
                  </span>
                )}
              </StyledTextField>
              <StyledTextField>
                <InputLabel
                  htmlFor="quantity_50L"
                  sx={{ fontFamily: "Roboto", fontWeight: "500" }}
                >
                  50L Keg
                </InputLabel>
                <StyledInput
                  placeholder="0"
                  type="number"
                  id="quantity_50L"
                  name="quantity_50L"
                  value={formData.quantity_50L || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onWheel={preventWheelChange}
                />
                {errors.quantity_50L && (
                  <span style={{ color: "#ef4444", fontSize: "11px" }}>
                    {errors.quantity_50L}
                  </span>
                )}
              </StyledTextField>
            </Box>
          </Grid2>

          <Grid2 size={[12]}>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              {/* Left Upload Documents */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InputLabel
                  htmlFor="UploadButton"
                  sx={{ fontFamily: "Roboto", fontWeight: "500" }}
                >
                  {isEdit ? "Uploaded Images" : "Upload Images"}
                </InputLabel>
                <input
                  type="file"
                  id={`UploadButton`}
                  style={{ display: "none" }}
                  data-testid="fileUpload"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                />
                <label htmlFor={`UploadButton`}>
                  <div style={uploadButtonContainer}>
                    <div style={uploadButton}>
                      <CloudUploadIcon
                        style={{ width: 18, height: 18, marginRight: 8 }}
                      />
                      <span>Upload</span>
                    </div>
                    {fileCount === 0 ? "No" : fileCount} file
                    {fileCount > 1 && "s"} chosen
                  </div>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    margin: "10px 0",
                    fontFamily: "Roboto",
                  }}
                >
                  {formData.fileUpload.map((file, index) => (
                    <FileTab key={index}>
                      <span>{file.name}</span>
                      <img
                        onClick={() => handleRemoveFile(file)}
                        alt="remove file"
                        src={crossIcon}
                        style={{ cursor: "pointer" }}
                      />
                    </FileTab>
                  ))}
                  {formData.existingImages.map((file, index) => (
                    <FileTab key={index}>
                      <span>{file.fileName}</span>
                      <img
                        onClick={() => handleRemoveFileUrl(file)}
                        alt="remove file"
                        src={crossIcon}
                        style={{ cursor: "pointer" }}
                      />
                    </FileTab>
                  ))}
                </div>
              </Box>
              {/* Right switch panel */}
              <Box
                sx={{
                  minWidth: { xs: "100%", md: 320 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  alignItems: { xs: "flex-start", md: "flex-end" },
                }}
              >
                {/* Pre-Bookings row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    justifyContent: { xs: "space-between", md: "flex-end" },
                  }}
                >
                  <Typography sx={{ fontFamily: "Roboto", fontWeight: "500" }}>
                    Pre-Bookings
                  </Typography>
                  <Switch
                    id="preBooking"
                    checked={preBooking}
                    onChange={(e) => setPreBooking(e.target.checked)}
                    onLabel="Enabled"
                    offLabel="Disabled"
                    trackHeight={40}
                    thumbSize={24}
                    trackWidth={127}
                  />
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>

        <Grid2
          container
          spacing={3}
          display="flex"
          justifyContent="flex-end"
          padding={3}
          width={"100%"}
          sx={{ marginTop: "16px" }}
        >
          <Button
            sx={{
              fontFamily: "poppins",
              width: "15vw",
              height: "6vh",
              borderRadius: "5px",
              borderColor: colors.primaryButtonOrange,
              color: colors.primaryButtonOrange,
            }}
            data-testid="reset-button"
            className="button"
            variant="outlined"
            onClick={handleOpen}
          >
            {buttonTextTwo}
          </Button>

          <Button
            type="submit"
            data-testid="submit-button"
            className="button"
            sx={{
              fontFamily: "poppins",
              width: "15vw",
              height: "6vh",
              borderRadius: "5px",
              boxShadow: "none",
              background: colors.primaryButtonOrange,
              "&:hover": {
                boxShadow: "none",
                background: colors.primaryButtonOrangeHover,
              },
            }}
            variant="contained"
          >
            {buttonTextOne}
          </Button>
        </Grid2>
      </form>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Box
          sx={{
            fontFamily: "poppins",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            padding: "35px",
            borderRadius: 2,
          }}
        >
          <Typography
            id="simple-modal-title"
            variant="h3"
            component="h1"
            sx={{ margin: "10px 0 30px" }}
          >
            Are you sure you want to reset?
          </Typography>
          <Button
            variant="contained"
            onClick={handleReset}
            sx={{
              padding: "18px 32px",
              mt: 3,
              fontWeight: "600",
              borderRadius: "5px",
              background: colors.primaryButtonOrange,
              color: "white",
            }}
          >
            Yes, I Am Sure
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              mt: 3,
              border: `1px solid ${colors.primaryButtonOrange}`,
              color: colors.primaryButtonOrange,
              borderRadius: "5px",
              padding: "18px 32px",
              fontWeight: "600",
            }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default ProductForm;
